import React, { useState, useMemo, useEffect } from 'react';
import styled from '@emotion/styled';
import GroupHeader from './GroupHeader';
import GroupItem from './GroupItem';
import { useI18n } from '~Hooks';

const Wrapper = styled.div`
  flex: 1;
`;

const Items = styled.div``;

export enum GROUP_TYPE {
  SINGLE = 'SINGLE',
  MULTIPLE = 'MULTIPLE',
}

export interface ExpandedGroupItem {
  id: string;
  name: string;
  price: number;
  imageUrl?: string | undefined;
  selected: boolean;
  isOutOfStock?: boolean;
}

export interface ExpandedGroup {
  type: GROUP_TYPE;
  title: string;
  expandedByDefault: boolean;
  onSelect: (id: string, groupId?: string) => void;
  items: ExpandedGroupItem[];
  required: boolean;
  preselectedVariations: string[];
}

const Index = ({ type, title, items, expandedByDefault, onSelect, required }: ExpandedGroup) => {
  const { i18n } = useI18n();
  const [opened, setOpened] = useState<boolean>(expandedByDefault);
  const selectedItems = useMemo(() => items.filter((item) => item.selected), [items]);

  const { description, color } = useMemo(() => {
    const isSelectedItems = Boolean(selectedItems.length);
    if (isSelectedItems) {
      return {
        description: i18n('SSK.SelectedVariations'),
        color: 'rgb(32, 153, 70)',
      };
    }
    if (required && !opened && !isSelectedItems) {
      return {
        description: i18n('SSK.SelectVariation'),
        color: 'rgb(227, 39, 59)',
      };
    }
    return {
      description: i18n('SSK.SelectOneOrMoreVariations'),
      color: 'rgb(97, 97, 100)',
    };
  }, [selectedItems, opened, required]);

  const handleSelect = (id: string) => {
    if (required) {
      setOpened(false);
    }

    onSelect(id);
  };

  const selectedVariationsNames = useMemo(() => {
    const selectedItemsNames = selectedItems.map((item) => item.name);

    if (selectedItemsNames.length > 1) {
      return `${selectedItemsNames[0]} + ${selectedItemsNames.length - 1} ${i18n('More')}`;
    }

    return selectedItemsNames[0];
  }, [selectedItems]);

  return (
    <Wrapper>
      <GroupHeader
        onSelect={() => setOpened((prev) => !prev)}
        title={title}
        description={description}
        opened={opened}
        color={color}
        variationsNames={selectedVariationsNames}
      />
      {opened && (
        <Items>
          {items.map((item) => (
            <GroupItem
              id={item.id}
              key={item.id}
              name={item.name}
              onSelect={() => handleSelect(item.id)}
              selected={item.selected}
              price={item.price}
              isOutOfStock={item.isOutOfStock}
              multi={type === GROUP_TYPE.MULTIPLE}
            />
          ))}
        </Items>
      )}
    </Wrapper>
  );
};

export default Index;
